// Settings › Job forms (M4 form builder): the forms techs fill on the iPad, where each is asked for,
// whether it's required to complete the job, and its questions. A change to the questions is a new
// version; a form already filled keeps the version it was filled on (forms.update). Owner and manager.

import { useCallback, useEffect, useState } from 'react';
import { client } from '../api.ts';
import { Loading } from '../shared/Loading.tsx';
import { shownText } from '../shared/pages.ts';
import { usePaged } from '../blueprint/paged.ts';
import { Sheet, SheetButtons, useWrite } from '../shared/EditSheet.tsx';
import { refusal } from '../history/customer-edit-model.ts';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { PricebookPicker } from '../job/EstimateEditor.tsx';
import { useLicenceKinds } from './Licences.tsx';
import {
  COMPLETED_WORK_KEY,
  CUSTOMER_CHOICES,
  emptyForm,
  emptyQuestion,
  FIELD_KINDS,
  formEditable,
  formForm,
  formInput,
  formScope,
  makesNewVersion,
  MAX_FORM_ITEMS,
  moveQuestion,
  offeredKinds,
  offJobInput,
  offJobWords,
  requiredChip,
  WHEN_CHOICES,
  type FieldKind,
  type FormForm,
  type JobForm,
  type OffJobPage,
  type OffJobSubmission,
  type QuestionForm,
} from './forms-model.ts';
import './setup.css';

interface Named {
  id: string;
  name: string;
  businessUnitId?: string | null;
}

export function FormsTab() {
  const [forms, setForms] = useState<JobForm[] | null>(null);
  const [units, setUnits] = useState<Named[]>([]);
  const [types, setTypes] = useState<Named[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<JobForm | 'new' | null>(null);
  const [offJob, setOffJob] = useState<JobForm | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  // while mirroring, ServiceTitan's forms are its own to change; Flowology's stay editable (BL-088)
  const mirroring = useMirror();
  const load = useCallback(async () => {
    try {
      setForms((await client.call<{ forms: JobForm[] }>('forms.list', {})).forms);
      setError(null);
    } catch (e) {
      setError(refusal(e));
    }
  }, []);
  useEffect(() => {
    void load();
    client
      .call<{ businessUnits: Named[] }>('businessUnits.list', {})
      .then((r) => setUnits(r.businessUnits))
      .catch(() => undefined);
    client
      .call<{ jobTypes: Named[] }>('jobTypes.list', {})
      .then((r) => setTypes(r.jobTypes))
      .catch(() => undefined);
  }, [load]);
  const done = (text: string) => {
    setOpen(null);
    setSaid(text);
    void load();
  };
  return (
    <section className="sheet setup-card" aria-labelledby="forms-title">
      <div className="side-head">
        <h2 id="forms-title">Your forms</h2>
        <button className="btn primary" onClick={() => setOpen('new')}>
          Add a form
        </button>
      </div>
      <p className="muted small">
        What techs fill in on the iPad: on every job, or only on one department’s or job type’s, for one kind
        of customer, or when an item is on the invoice. A required form must be done before the office
        completes the job; one required on arrival comes before the work starts.
      </p>
      {said && (
        <p className="notice ok" role="status">
          {said}
        </p>
      )}
      {!forms ? (
        <Loading error={error} what="the forms" retry={() => void load()} />
      ) : (
        <ul className="setup-list">
          {forms.map((f) => (
            <li key={f.id} className={f.active ? '' : 'off'}>
              <div>
                <b>{f.name}</b>
                <span className="muted small"> · {formScope(f, units, types)}</span>
                <div className="form-chips">
                  {requiredChip(f) && <span className="chip say">{requiredChip(f)}</span>}
                  <span className="chip say">Version {f.version ?? 1}</span>
                  {!f.active && <span className="chip say off">Retired</span>}
                </div>
                <p className="muted small">
                  {f.fields.length} question{f.fields.length === 1 ? '' : 's'}
                  {f.submissions ? ` · filled in ${f.submissions} time${f.submissions === 1 ? '' : 's'}` : ''}
                </p>
                {offJobWords(f) && (
                  <button className="btn link sm" onClick={() => setOffJob(f)}>
                    {offJobWords(f)}
                  </button>
                )}
              </div>
              {formEditable(f, mirroring) ? (
                <button className="btn quiet sm" aria-label={`Edit ${f.name}`} onClick={() => setOpen(f)}>
                  Edit
                </button>
              ) : (
                <p className="muted small">{MIRROR_LINE}</p>
              )}
            </li>
          ))}
        </ul>
      )}
      {offJob && <OffJobSheet form={offJob} onClose={() => setOffJob(null)} />}
      {open && (open === 'new' || formEditable(open, mirroring)) && (
        <FormSheet
          form={open === 'new' ? null : open}
          units={units}
          types={types}
          onDone={done}
          onClose={() => setOpen(null)}
        />
      )}
    </section>
  );
}

/** A form's submissions ServiceTitan filed on no job (#81): the company's own, read only. */
function OffJobSheet({ form, onClose }: { form: JobForm; onClose: () => void }) {
  // a page at a time, "Load more" reading on (list caps M10): years of a company's own forms
  const list = usePaged<OffJobSubmission, OffJobPage>(
    'forms.offJob',
    offJobInput(form.id),
    (p) => p.submissions,
  );
  const rows = list.rows;
  const total = list.first?.total ?? rows?.length ?? 0;
  return (
    <Sheet title={`${form.name}: on no job`} wide onClose={onClose}>
      {!rows ? (
        <Loading error={list.error} what="the forms" />
      ) : (
        <>
          <ul className="setup-list">
            {rows.map((r) => (
              <li key={r.id}>
                <div>
                  <b>{new Date(r.submittedAt).toLocaleDateString()}</b>
                  {r.by && <span className="muted small"> · {r.by}</span>}
                  {r.answers.map((a) => (
                    <p key={a.question} className="small">
                      <span className="muted">{a.question}:</span> {a.answer}
                    </p>
                  ))}
                </div>
              </li>
            ))}
          </ul>
          {(list.more || list.busy) && (
            <p className="row setup-more">
              <span className="muted small">{shownText(rows.length, total)}</span>
              <button type="button" className="btn quiet" disabled={list.busy} onClick={() => list.more?.()}>
                {list.busy ? 'Loading…' : 'Load more'}
              </button>
            </p>
          )}
        </>
      )}
    </Sheet>
  );
}

function FormSheet({
  form,
  units,
  types,
  onDone,
  onClose,
}: {
  form: JobForm | null;
  units: Named[];
  types: Named[];
  onDone: (said: string) => void;
  onClose: () => void;
}) {
  const [f, setF] = useState<FormForm>(() => (form ? formForm(form) : emptyForm()));
  const w = useWrite(JSON.stringify(f));
  const life = useWrite(`life:${form?.id ?? ''}`);
  const completedWork = form?.key === COMPLETED_WORK_KEY;
  const kinds = offeredKinds();
  const { kinds: licenceKinds } = useLicenceKinds(true);
  const set = (patch: Partial<FormForm>) => {
    setF({ ...f, ...patch });
    w.setError(null);
  };
  const setQ = (i: number, patch: Partial<QuestionForm>) =>
    set({ questions: f.questions.map((q, j) => (j === i ? { ...q, ...patch } : q)) });
  const typesHere = types.filter(
    (t) => !f.businessUnitId || !t.businessUnitId || t.businessUnitId === f.businessUnitId,
  );
  const newVersion = makesNewVersion(form, f);
  const save = async () => {
    const r = await w.run<JobForm>(form ? 'forms.update' : 'forms.define', formInput(form, f));
    if (r)
      onDone(
        form
          ? r.version !== form.version
            ? `${r.name} is saved as version ${r.version}. Forms already filled keep version ${form.version}.`
            : `${r.name} is saved.`
          : `${r.name} is added. Techs see it on the iPad on the jobs it’s for.`,
      );
  };
  const flip = async () => {
    if (!form) return;
    const r = await life.run<JobForm>(form.active ? 'forms.retire' : 'forms.restore', {
      input: { id: form.id, rowVersion: form.rowVersion },
    });
    if (r)
      onDone(
        r.active
          ? `${r.name} is in use again.`
          : `${r.name} is retired. Forms already filled stay as they are.`,
      );
  };
  return (
    <Sheet title={form ? form.name : 'Add a job form'} wide onClose={onClose}>
      <label className="f">
        Name
        <input className="field" value={f.name} onChange={(e) => set({ name: e.target.value })} autoFocus />
      </label>
      <label className="checkrow">
        <input
          type="checkbox"
          checked={f.requiredOnCompletion}
          disabled={completedWork}
          onChange={(e) => set({ requiredOnCompletion: e.target.checked })}
        />{' '}
        Required to complete the job
        {completedWork && <span className="muted small"> (always, for Completed work)</span>}
      </label>
      {f.requiredOnCompletion && !completedWork && (
        <label className="f">
          When the tech fills it in
          <select
            className="field"
            value={f.requiredAt}
            onChange={(e) => set({ requiredAt: e.target.value as FormForm['requiredAt'] })}
          >
            {WHEN_CHOICES.map(([v, words]) => (
              <option key={v} value={v}>
                {words}
              </option>
            ))}
          </select>
        </label>
      )}
      {!completedWork && (
        <>
          <label className="checkrow">
            <input
              type="checkbox"
              checked={f.licensedSigner}
              onChange={(e) =>
                set({
                  licensedSigner: e.target.checked,
                  licenceKindId: e.target.checked ? f.licenceKindId : '',
                })
              }
            />{' '}
            Only someone licensed fills it in (the office can’t for them)
          </label>
          {f.licensedSigner && (
            <label className="f">
              The licence they hold
              <select
                className="field"
                value={f.licenceKindId}
                onChange={(e) => set({ licenceKindId: e.target.value })}
              >
                <option value="">Choose…</option>
                {(licenceKinds ?? [])
                  .filter((k) => k.active || k.id === f.licenceKindId)
                  .map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.name}
                    </option>
                  ))}
              </select>
              {licenceKinds && !licenceKinds.length && (
                <span className="muted small">
                  Add the licences you track first, in Settings › Departments and job types.
                </span>
              )}
            </label>
          )}
        </>
      )}
      <div className="fgrid">
        <label className="f">
          Department
          <select
            className="field"
            value={f.businessUnitId}
            onChange={(e) => set({ businessUnitId: e.target.value, jobTypeId: '' })}
          >
            <option value="">Every department</option>
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </label>
        <label className="f">
          Job type
          <select className="field" value={f.jobTypeId} onChange={(e) => set({ jobTypeId: e.target.value })}>
            <option value="">Every job type</option>
            {typesHere.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
        {!completedWork && (
          <label className="f">
            Customers
            <select
              className="field"
              value={f.customerKind}
              onChange={(e) => set({ customerKind: e.target.value as FormForm['customerKind'] })}
            >
              {CUSTOMER_CHOICES.map(([v, words]) => (
                <option key={v} value={v}>
                  {words}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {!completedWork && (
        <fieldset className="plain form-items">
          <legend className="h4">Only when the invoice has</legend>
          <p className="muted small">
            {f.items.length
              ? 'Asked for once one of these is on the job’s invoice.'
              : 'No items: asked for whatever is on the invoice.'}
          </p>
          {f.items.length > 0 && (
            <ul className="form-item-list">
              {f.items.map((i) => (
                <li key={i.id}>
                  <span>{i.name}</span>
                  <button
                    type="button"
                    className="btn quiet sm"
                    aria-label={`Take ${i.name} off`}
                    onClick={() => set({ items: f.items.filter((x) => x.id !== i.id) })}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
          {f.items.length < MAX_FORM_ITEMS && (
            <PricebookPicker
              label="Add an item"
              onPick={(item) =>
                f.items.some((x) => x.id === item.id)
                  ? undefined
                  : set({ items: [...f.items, { id: item.id, name: item.name }] })
              }
            />
          )}
        </fieldset>
      )}

      <fieldset className="plain questions">
        <legend className="h4">Questions</legend>
        <ol className="question-list">
          {f.questions.map((q, i) => {
            const n = i + 1;
            // a kind the builder doesn't offer yet stays choosable on a question that already has it
            const here = kinds.some(([k]) => k === q.type)
              ? kinds
              : [...kinds, FIELD_KINDS.find(([k]) => k === q.type)!];
            return (
              <li key={i} className="question" aria-label={`Question ${n}`}>
                <div className="fgrid">
                  <label className="f wide">
                    Question {n}
                    <input
                      className="field"
                      value={q.label}
                      onChange={(e) => setQ(i, { label: e.target.value })}
                      placeholder="What the tech answers, e.g. Leak found"
                    />
                  </label>
                  <label className="f">
                    Kind of answer
                    <select
                      className="field"
                      value={q.type}
                      onChange={(e) => setQ(i, { type: e.target.value as FieldKind })}
                    >
                      {here.map(([k, label]) => (
                        <option key={k} value={k}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                {q.type === 'choice' && (
                  <label className="f">
                    Answers to pick from, one per line
                    <textarea
                      className="field area"
                      value={q.options}
                      onChange={(e) => setQ(i, { options: e.target.value })}
                    />
                  </label>
                )}
                <label className="f">
                  Help for the tech (optional)
                  <input
                    className="field"
                    value={q.help}
                    maxLength={200}
                    onChange={(e) => setQ(i, { help: e.target.value })}
                    placeholder="A line under the question, e.g. Measure at the hose bib"
                  />
                </label>
                <div className="row q-acts">
                  <label className="checkrow">
                    <input
                      type="checkbox"
                      checked={q.required}
                      onChange={(e) => setQ(i, { required: e.target.checked })}
                    />{' '}
                    Required
                  </label>
                  <button
                    type="button"
                    className="btn quiet sm"
                    aria-label={`Move question ${n} up`}
                    disabled={i === 0}
                    onClick={() => set({ questions: moveQuestion(f.questions, i, -1) })}
                  >
                    Up
                  </button>
                  <button
                    type="button"
                    className="btn quiet sm"
                    aria-label={`Move question ${n} down`}
                    disabled={i === f.questions.length - 1}
                    onClick={() => set({ questions: moveQuestion(f.questions, i, 1) })}
                  >
                    Down
                  </button>
                  <button
                    type="button"
                    className="btn quiet sm"
                    aria-label={`Remove question ${n}`}
                    disabled={f.questions.length === 1}
                    onClick={() => set({ questions: f.questions.filter((_, j) => j !== i) })}
                  >
                    Remove
                  </button>
                </div>
              </li>
            );
          })}
        </ol>
        <button
          type="button"
          className="btn soft"
          onClick={() => set({ questions: [...f.questions, emptyQuestion()] })}
        >
          Add a question
        </button>
      </fieldset>

      {form && (
        <p className="muted small">
          {newVersion
            ? `Saving makes version ${(form.version ?? 1) + 1}. Forms already filled keep the version they were filled on.`
            : `Version ${form.version ?? 1}. Changing the questions makes a new version; forms already filled keep theirs.`}
        </p>
      )}
      <SheetButtons
        w={w}
        label={form ? 'Save' : 'Add form'}
        busyLabel="Saving…"
        onSave={() => void save()}
        onCancel={onClose}
      />
      {form && !completedWork && (
        <div className="row life">
          <button type="button" className="btn soft" disabled={life.busy} onClick={() => void flip()}>
            {life.busy
              ? form.active
                ? 'Retiring…'
                : 'Bringing it back…'
              : form.active
                ? 'Retire this form'
                : 'Use it again'}
          </button>
          {life.error && (
            <p className="error small" role="alert">
              {life.error}
            </p>
          )}
        </div>
      )}
    </Sheet>
  );
}
